/**
 * Hand a file to the Register Import screen (Register Management, ExcelUpload) from outside it:
 * the assistant's `ui.registers.open_import` offers the file, navigates to the page, and the page
 * takes it when it handles its `?registerId=…&view=import` link, opens the import dialog for that
 * register with the file loaded (as if the user had dropped it), and says whether it did.
 * One offer at a time: a new offer ends the last one. Page memory only.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/** The Register Management route and its query (RegisterManagement.vue handleNotificationDeepLink). */
export const REGISTER_IMPORT_ROUTE = Object.freeze({ NAME: 'RegistersAdmin', VIEW: 'import' });

/** How long the page has to open the dialog before the offer ends unanswered. */
export const REGISTER_IMPORT_WAIT_MS = 20000;

export const REGISTER_IMPORT_OUTCOME = Object.freeze({ OPENED: 'opened', NOT_FOUND: 'not_found', TIMED_OUT: 'timed_out' });

let pending = null;

const sameId = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

/**
 * @param {{ registerId: string, file: File, waitMs?: number, signal?: AbortSignal|null }} offer
 * @returns {Promise<{ outcome: string, register?: string }>} OPENED with the register's name, NOT_FOUND, or TIMED_OUT
 */
export function offerImportFile({ registerId, file, waitMs = REGISTER_IMPORT_WAIT_MS, signal = null }) {
  if (pending) pending.settle({ outcome: REGISTER_IMPORT_OUTCOME.TIMED_OUT });
  return new Promise((resolve) => {
    const entry = { registerId, file, settle: null };
    let timer = null;
    const onAbort = () => entry.settle({ outcome: REGISTER_IMPORT_OUTCOME.TIMED_OUT });
    entry.settle = (result) => {
      if (pending === entry) pending = null;
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve(result);
    };
    pending = entry;
    timer = setTimeout(onAbort, waitMs);
    if (signal) signal.addEventListener('abort', onAbort);
  });
}

/**
 * The page takes the file offered for `registerId` (null when none is waiting for it).
 * @returns {{ file: File, opened: (registerName: string) => void } | null}
 */
export function takeImportFile(registerId) {
  const entry = pending;
  if (!entry || !sameId(entry.registerId, registerId)) return null;
  pending = null;
  return { file: entry.file, opened: (registerName) => entry.settle({ outcome: REGISTER_IMPORT_OUTCOME.OPENED, register: registerName }) };
}

/** The page could not find `registerId` in its list: the offer for it ends as NOT_FOUND. */
export function declineImportFile(registerId) {
  const entry = pending;
  if (entry && sameId(entry.registerId, registerId)) entry.settle({ outcome: REGISTER_IMPORT_OUTCOME.NOT_FOUND });
}
